import { getAccountInfo, getRefundInfo } from '@/api';
import { IRefundInfoForm } from '@/types/common.type';
import { CASH_PICKUP_NATIONS } from '@repo/constants';
import { isNationIncluded } from '@repo/utils';
import { useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useQuery } from 'react-query';

const useGetAccountInfo = (
  nationality: string,
  passportNumber: string,
  setValue: UseFormSetValue<IRefundInfoForm>,
) => {
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedAccountInfo, setSelectedAccountInfo] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedTown, setSelectedTown] = useState('');
  const [isBankCardInquiry, setIsBankCardInquiry] = useState(false);
  const [isHkgNation, setIsHkgNation] = useState(false);
  const [isOpenLoad, setIsOpenLoad] = useState(false);

  const { data: beforeAccountInfo }: any = useQuery(
    'getAccountInfo',
    () => getAccountInfo(passportNumber),
    {
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          setIsOpenLoad(true);
        }
      },
    },
  );

  const setAccountInfo = (data: any, nationality: string) => {
    setValue('firstName', data.firstName);
    setValue('lastName', data.lastName);
    setValue('phoneNumber', data.phoneNumber);
    setValue('transferMethod', data.transferMethod);
    setValue('aliPayAccount', data.aliPayAccount);
    setValue('cardNumber', data.cardNumber);
    setValue('bank', data.bank);
    setValue('bankAccount', data.bankAccount);
    setValue('accountInfo', data.accountInfo);
    setValue('routingNumber', data.routingNumber);
    setValue('stateProvinceRegion', data.stateProvinceRegion);
    setValue('zipPostalCode', data.zipPostalCode);
    setValue('address', data.address);
    setValue('branchInformation', data.branchInformation);
    setValue('bsbNumber', data.bsbNumber);
    setValue('transitNumber', data.transitNumber);
    setValue('cashPickup', data.cashPickup);
    setValue('ibanNumber', data.ibanNumber);
    setValue('swiftCode', data.swiftCode);
    setValue('birth', data.birth);
    setValue('addressDetail', data.addressDetail);
    setValue('addressDetail2', data.addressDetail2);
    setValue('accountType', data.accountType);
    setValue('institutionNumber', data.institutionNumber);
    setValue('gender', data.gender);
    setValue('citizenId', data.citizenId);
    setValue('country', data.country);
    setValue('firstNameEng', data.firstNameEng);
    setValue('lastNameEng', data.lastNameEng);
    setValue('directInput', data.directInput);
    setValue('accountHolder', data.accountHolder);
    setValue('stateProvinceRegionEng', data.stateProvinceRegionEng);
    setValue('cityEng', data.cityEng);
    setValue('addressEng', data.addressEng);
    setValue('addressDetailEng', data.addressDetailEng);
    setSelectedAccountInfo(data.accountInfo);
    setSelectedAccountType(data.accountType);
    setSelectedBank(data.bank);
    setSelectedGender(data.gender);
    setIsBankCardInquiry(data.isBankCardInquiry || false);
    setIsHkgNation(data.isHkgNation || false);
    setSelectedRegion(data.stateProvinceRegion);
    setValue('city', data.city);
    setSelectedTown(data.city);
    if (isNationIncluded(CASH_PICKUP_NATIONS, nationality)) {
      setSelectedBank(data.cashPickup);
    }
  };

  const { refetch, isFetching } = useQuery<any, string>(
    'RefundInfo',
    () => getRefundInfo(passportNumber),
    {
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: async (data) => {
        if (!data) {
          return;
        }
        const { accountInfo } = data;
        setAccountInfo(accountInfo, nationality);
      },
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      refetch();
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setValue('aliPayAccount', '');
    setValue('cardNumber', '');
    setValue('bank', '');
    setValue('bankAccount', '');
    setValue('accountInfo', '');
    setValue('routingNumber', '');
    setValue('city', '');
    setValue('stateProvinceRegion', '');
    setValue('zipPostalCode', '');
    setValue('address', '');
    setValue('branchInformation', '');
    setValue('bsbNumber', '');
    setValue('transitNumber', '');
    setValue('cashPickup', '');
    setValue('ibanNumber', '');
    setValue('swiftCode', '');
    setValue('birth', '');
    setValue('addressDetail', '');
    setValue('addressDetail2', '');
    setValue('accountType', '');
    setValue('institutionNumber', '');
    setValue('gender', '');
    setValue('citizenId', '');
    setValue('country', '');
    setValue('firstNameEng', '');
    setValue('lastNameEng', '');
    setValue('accountHolder', '');
    setIsBankCardInquiry(false);
    setSelectedBank('');
    setSelectedRegion('');
    setSelectedTown('');
    setSelectedGender('');
  }, [isHkgNation]);

  return {
    selectedGender,
    setSelectedGender,
    selectedAccountInfo,
    setSelectedAccountInfo,
    selectedAccountType,
    setSelectedAccountType,
    selectedBank,
    setSelectedBank,
    selectedRegion,
    setSelectedRegion,
    selectedTown,
    setSelectedTown,
    isBankCardInquiry,
    setIsBankCardInquiry,
    isHkgNation,
    setIsHkgNation,
    isOpenLoad,
    setIsOpenLoad,
    beforeAccountInfo,
    isFetching,
    setAccountInfo,
  };
};
export default useGetAccountInfo;
